import type { ICreatorScriptUserInputsPayload } from "@entities/aiWorkspace/lib/types/creatorScriptJob";

export type ScriptPlatform = "instagram" | "youtube";

export type ScriptCreator = {
  id: string;
  jobId: string;
  platform: ScriptPlatform;
  username: string;
  name: string;
  image?: string;
  followers?: number;
};

export type ScriptSettings = {
  format: string;
  duration: string;
  tone: string;
  language: string;
};

export type ScriptDraftStatus = "idle" | "generating" | "ready";

export type ScriptDraft = {
  id: string;
  creator: ScriptCreator;
  brief: string;
  settings: ScriptSettings;
  markdown: string;
  status: ScriptDraftStatus;
};

export type ScriptAiContext = {
  aiWorkspaceId: string;
  creatorHandle: string;
  platform: ScriptPlatform;
  brief: string;
  format: string;
  duration: string;
  tone: string;
  language: string;
};

export const DEFAULT_SCRIPT_SETTINGS: ScriptSettings = {
  format: "Reel",
  duration: "30s",
  tone: "Creator-native",
  language: "English",
};

export const SCRIPT_FORMATS = ["Reel", "Video", "Story"] as const;
export const SCRIPT_DURATIONS = [
  { value: "15s", label: "~15 seconds" },
  { value: "30s", label: "~30 seconds" },
  { value: "45s", label: "~45 seconds" },
  { value: "60s", label: "~60 seconds" },
] as const;
export const SCRIPT_TONES = [
  "Creator-native",
  "Balanced",
  "Brand-native",
] as const;
export const SCRIPT_LANGUAGES = [
  "English",
  "Hindi",
  "Hinglish",
  "Tamil",
  "Telugu",
] as const;

export function readScriptGenerationMasterPrompt(
  saved?: Partial<ICreatorScriptUserInputsPayload>
): ICreatorScriptUserInputsPayload | undefined {
  if (!saved) return undefined;
  const inputs = {
    brief: saved.brief || "",
    format: saved.format || "",
    duration: saved.duration || "",
    toneBalance: saved.toneBalance || "",
    language: saved.language || "",
  };
  if (!Object.values(inputs).some((value) => value.trim())) return undefined;
  return inputs;
}

export function matchedScriptOption(
  value: unknown,
  options: readonly string[],
  fallback: string
) {
  if (typeof value !== "string" || !value.trim()) return fallback;
  return (
    options.find((option) => option.toLowerCase() === value.toLowerCase()) ||
    value
  );
}

export function creatorKey(platform: string, username: string) {
  return `${platform.toLowerCase()}:${username.trim().replace(/^@+/, "").toLowerCase()}`;
}
